/**
 * Descoberta: sugestões do campo de busca (T2.8).
 *
 * A regra de correspondência, o universo público e as cotas por grupo vivem na RPC
 * `suggest_search()`; este módulo só normaliza a entrada, repassa o cancelamento e valida a forma
 * da resposta. Nada do histórico do usuário é enviado ou guardado (J15).
 *
 * Erros são lançados como `DiscoveryDataError` com `code` tipado — o React Query precisa da
 * exceção para chegar ao estado de erro, e a tela decide a mensagem pelo código, não pelo texto.
 */
import type { LegalLocale } from './legal';

export const SUGGESTION_KINDS = ['category', 'service', 'business'] as const;
export type SuggestionKind = (typeof SUGGESTION_KINDS)[number];

export interface SearchSuggestion {
  kind: SuggestionKind;
  label: string;
  /** Cidade do negócio; `null` para categoria e serviço. */
  detail: string | null;
  /** Slug da categoria/negócio ou nome normalizado do serviço. */
  target: string;
  category_id: string | null;
  similarity: number;
}

export interface SuggestSearchInput {
  q: string;
  locale: LegalLocale;
  limit?: number;
}

export type DiscoveryErrorCode =
  | 'unauthenticated'
  | 'invalid_input'
  | 'invalid_response'
  | 'aborted'
  | 'network'
  | 'unknown';

export class DiscoveryDataError extends Error {
  readonly code: DiscoveryErrorCode;

  constructor(code: DiscoveryErrorCode, options?: { cause?: unknown }) {
    super(code, options);
    this.name = 'DiscoveryDataError';
    this.code = code;
  }
}

type RpcResult = { data: unknown; error: unknown };

export interface DiscoveryClient {
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): {
    // O client do Supabase é injetado para manter o módulo testável sem rede.
    abortSignal(signal: AbortSignal): PromiseLike<RpcResult>;
  };
}

/** Limites espelhados da RPC — evitam uma ida à rede que só voltaria vazia ou com 22023. */
export const SUGGEST_MIN_CHARS = 2;
export const SUGGEST_MAX_CHARS = 60;
export const SUGGEST_DEFAULT_LIMIT = 8;

/** Caracteres úteis como a RPC os conta: sem acento, só letras/dígitos. */
function usefulLength(q: string): number {
  return q
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '').length;
}

export async function suggestSearch(
  client: DiscoveryClient,
  { q, locale, limit = SUGGEST_DEFAULT_LIMIT }: SuggestSearchInput,
  signal: AbortSignal = new AbortController().signal,
): Promise<SearchSuggestion[]> {
  const query = q.trim();
  if (query.length > SUGGEST_MAX_CHARS) throw new DiscoveryDataError('invalid_input');
  if (usefulLength(query) < SUGGEST_MIN_CHARS) return [];

  let result: RpcResult;
  try {
    result = await client
      .rpc('suggest_search', { p_q: query, p_locale: locale, p_limit: limit })
      .abortSignal(signal);
  } catch (cause) {
    throw new DiscoveryDataError(signal.aborted ? 'aborted' : 'network', { cause });
  }

  if (result.error) {
    throw new DiscoveryDataError(signal.aborted ? 'aborted' : classify(result.error), {
      cause: result.error,
    });
  }
  if (result.data == null) return [];
  if (!Array.isArray(result.data)) throw new DiscoveryDataError('invalid_response');
  return result.data.map(parseSuggestion);
}

function classify(error: unknown): DiscoveryErrorCode {
  const { code, message } = (error ?? {}) as { code?: unknown; message?: unknown };
  if (code === '42501' || code === 'PGRST301') return 'unauthenticated';
  if (code === '22023') return 'invalid_input';
  if (typeof message === 'string' && /fetch|network/i.test(message)) return 'network';
  return 'unknown';
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function parseSuggestion(row: unknown): SearchSuggestion {
  const r = (row ?? {}) as Record<string, unknown>;
  const similarity = typeof r.similarity === 'string' ? Number(r.similarity) : r.similarity;
  if (
    !SUGGESTION_KINDS.includes(r.kind as SuggestionKind) ||
    typeof r.label !== 'string' ||
    typeof r.target !== 'string' ||
    !isNullableString(r.detail) ||
    !isNullableString(r.category_id) ||
    typeof similarity !== 'number' ||
    !Number.isFinite(similarity)
  ) {
    throw new DiscoveryDataError('invalid_response');
  }
  return {
    kind: r.kind as SuggestionKind,
    label: r.label,
    detail: r.detail,
    target: r.target,
    category_id: r.category_id,
    similarity,
  };
}
